import { useEffect, useState } from 'react';

import Head from '@/components/Head';
import { GetServerSideProps } from 'next';
import { getTheme } from '@/utils/helpers';
import { useTheme } from '@/contexts/ThemeContext';
import Terms from '@/components/V13/Terms';

export default function TermsConditions(): JSX.Element {
  const theme = useTheme();
  const { slug, landingPage } = theme;
  const TITLE = process.env.NEXT_PUBLIC_TITLE;
  const COMPANY = process.env.NEXT_PUBLIC_COMPANY_NAME;
  const HOSTNAME = process.env.NEXT_PUBLIC_HOSTNAME;
  const IS_BANK = process.env.NEXT_PUBLIC_IS_BANK === 'true';
  const ADDRESS = process.env.NEXT_PUBLIC_ADDRESS;
  const COMPANY_ADDRESS = IS_BANK
    ? ADDRESS
    : slug === 'dl_pt'
    ? 'Rua Rocha do Conde de Óbidos, Armazém 114, Nave P0, 1350-352, Lisbon, Portugal'
    : ADDRESS;
  const REGISTRATION_NUMBER = process.env.NEXT_PUBLIC_REGISTRATION_NUMBER;
  const COMPANY_NUMBER = IS_BANK
    ? REGISTRATION_NUMBER
    : slug === 'dl_pt'
    ? '514083883223'
    : REGISTRATION_NUMBER;

  const [companyAddressWidth, setCompanyAddressWidth] = useState<number>();
  const [companyPostcodeWidth, setCompanyPostcodeWidth] = useState<number>();
  const [companyNameWidth, setCompanyNameWidth] = useState<number>();
  const [companyNumberWidth, setCompanyNumberWidth] = useState<number>();

  const isEstonianAddress = COMPANY_ADDRESS === ADDRESS;
  const estonianAddress = isEstonianAddress && COMPANY_ADDRESS.split(',');
  const isDlAssistant = ['v12', 'v13'].includes(landingPage);

  useEffect(() => {
    if (isEstonianAddress) {
      const companyAddressCanvas: HTMLCollectionOf<HTMLCanvasElement> =
        document.getElementsByClassName(
          'companyAddress'
        ) as HTMLCollectionOf<HTMLCanvasElement>;

      for (let i = 0; i < companyAddressCanvas.length; i++) {
        const companyAddressContext = companyAddressCanvas[i].getContext('2d');
        companyAddressContext.font = '500 14px Inter var';
        companyAddressContext.fillStyle = 'rgba(107, 114, 128, 1)';
        companyAddressContext.fillText(` ${estonianAddress[0]} , `, 0, 15);
        setCompanyAddressWidth(
          companyAddressContext.measureText(`${estonianAddress[0]} , `).width
        );
      }

      const companyPostcodeCanvas: HTMLCollectionOf<HTMLCanvasElement> =
        document.getElementsByClassName(
          'companyPostcode'
        ) as HTMLCollectionOf<HTMLCanvasElement>;
      for (let i = 0; i < companyPostcodeCanvas.length; i++) {
        const companyPostcodeContext =
          companyPostcodeCanvas[i].getContext('2d');
        companyPostcodeContext.font = '500 14px Inter var';
        companyPostcodeContext.fillStyle = 'rgba(107, 114, 128, 1)';
        companyPostcodeContext.fillText(`${estonianAddress[1]} `, 0, 15);
        setCompanyPostcodeWidth(
          companyPostcodeContext.measureText(`${estonianAddress[1]} `).width
        );
      }
    }

    if (COMPANY) {
      const companyNameCanvas: HTMLCollectionOf<HTMLCanvasElement> =
        document.getElementsByClassName(
          'companyName'
        ) as HTMLCollectionOf<HTMLCanvasElement>;

      for (let i = 0; i < companyNameCanvas.length; i++) {
        const companyNameContext = companyNameCanvas[i].getContext('2d');
        companyNameContext.font = '500 14px Inter var';
        companyNameContext.fillStyle = 'rgba(107, 114, 128, 1)';
        companyNameContext.fillText(` ${COMPANY} `, 0, 15);
        setCompanyNameWidth(
          companyNameContext.measureText(` ${COMPANY} `).width
        );
      }
    }

    if (COMPANY_NUMBER) {
      const companyNumberCanvas: HTMLCollectionOf<HTMLCanvasElement> =
        document.getElementsByClassName(
          'companyNumber'
        ) as HTMLCollectionOf<HTMLCanvasElement>;

      for (let i = 0; i < companyNumberCanvas.length; i++) {
        const companyNumberContext = companyNumberCanvas[i].getContext('2d');
        companyNumberContext.font = '500 14px Inter var';
        companyNumberContext.fillStyle = 'rgba(107, 114, 128, 1)';
        companyNumberContext.fillText(` ${COMPANY_NUMBER} `, 0, 15);
        setCompanyNumberWidth(
          companyNumberContext.measureText(` ${COMPANY_NUMBER} `).width
        );
      }
    }
  }, [
    companyAddressWidth,
    companyPostcodeWidth,
    companyNameWidth,
    companyNumberWidth,
  ]);

  const companyName = (
    <canvas className="companyName" height="21px" width={companyNameWidth} />
  );
  const companyNumber = (
    <canvas
      className="companyNumber"
      height="21px"
      width={companyNumberWidth}
    />
  );
  const estoniaAddress = (
    <>
      <canvas
        className="companyAddress"
        height="21px"
        width={companyAddressWidth}
      />
      <canvas
        className="companyPostcode"
        height="21px"
        width={companyPostcodeWidth}
      />
    </>
  );

  if (isDlAssistant) {
    return (
      <>
        <Head title={`${TITLE} - Terms and Conditions`} />
        <div className="bg-cool-gray-50">
          <div className="mx-auto py-16 sm:px-2 lg:px-8">
            <h1
              className="text-3xl text-center leading-8 font-bold tracking-tight text-gray-900 mb-10 sm:text-4xl sm:leading-10"
              id="terms"
            >
              Terms and Conditions
            </h1>
            <Terms />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title={`${TITLE} - Terms and Conditions`} />
      <div className="bg-cool-gray-50">
        <div className="mx-auto py-16 sm:px-2 lg:px-8">
          <h1
            className="text-3xl text-center leading-8 font-bold tracking-tight text-gray-900 mb-10 sm:text-4xl sm:leading-10"
            id="terms"
          >
            Terms and Conditions
          </h1>

          <div className="bg-white rounded shadow px-4 md:px-12 py-12 text-gray-500">
            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              1. WHAT&apos;S IN THESE TERMS?
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                1.1 What do these terms cover? These are the terms and
                conditions on which we supply our services to you.
              </li>
              <li className="mt-2">
                1.2 Why you should read them. Please read these terms carefully
                before you submit your order to us. These terms tell you who we
                are, how we will provide services to you, how you and we may
                change or end the contract, what to do if there is a problem and
                other important information. If you think that there is a
                mistake in these terms, please contact us to discuss.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              2. WHO WE ARE AND HOW TO CONTACT US
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2 flex flex-wrap">
                2.1 Company details:
                {companyName}
                (company number
                {COMPANY_NUMBER && companyNumber}) (we and us) is a company
                registered in{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                .{companyName} also trades under the trading name(s) {HOSTNAME}.
                Our main trading address is{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                We operate the website {HOSTNAME}.
              </li>
              <li className="mt-2 flex flex-wrap">
                2.2 Contacting us: To contact us, email us at contact@{HOSTNAME}
                . You may also contact us in writing at {companyName},{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                .
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              3. BY USING OUR SITE YOU ACCEPT THESE TERMS
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li>
                3.1 By using our site, you confirm that you accept these terms
                of use and that you agree to comply with them.
              </li>
              <li className="mt-2">
                3.2 You are also responsible for ensuring that all persons who
                access our site through your internet connection are aware of
                these terms of use and other applicable terms and conditions,
                and that they comply with them.
              </li>
              <li className="mt-2">
                3.3 If you do not agree to these terms, you must not use our
                site.
              </li>
              <li className="mt-2">
                3.4 We recommend that you print a copy of these terms for future
                reference.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              4. THERE ARE OTHER TERMS THAT MAY APPLY TO YOU
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                4.1 These terms of use refer to the following additional terms,
                which also apply to your use of our site:
                <ul className="list-none pl-10 mt-2">
                  <li className="mt-2">
                    4.1.1 Our <a href="/privacy-policy">Privacy Policy</a>. See
                    further under How we may use your personal information.
                  </li>
                  <li className="mt-2">
                    4.1.2 Our Acceptable{' '}
                    <a href={'#acceptancePolicy'}>Use Policy</a>, which sets out
                    the permitted uses and prohibited uses of our site. When
                    using our site, you must comply with our Acceptable Use
                    Policy.{' '}
                  </li>
                  <li className="mt-2">
                    4.1.3 Our Cookie Policy, which sets out information about
                    the cookies on our site. If you purchase services from our
                    site, our Website{' '}
                    <a href={'#supplyOfServices'}>
                      terms and conditions of supply
                    </a>{' '}
                    will apply to your purchase.
                  </li>
                </ul>
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              5. WE MAY MAKE CHANGES TO THESE TERMS
            </h3>
            <ul className="pl-10 mt-5">
              <li className="mt-2">
                We amend these terms from time to time. Every time you wish to
                use our site, please check these terms to ensure you understand
                the terms that apply at that time. These terms were most
                recently updated on 18/07/2022 when we changed clause 12.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              6. WE MAY MAKE CHANGES TO OUR SITE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We may update and change our site from time to time to reflect
                changes to our products, our users’ needs and our business
                priorities or for any other reason.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              7. WE MAY SUSPEND OR WITHDRAW OUR SITE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We do not guarantee that our site, or any content on it, will
                always be available or be uninterrupted. We may suspend or
                withdraw or restrict the availability of all or any part of our
                site for business and operational reasons.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              8. WE MAY TRANSFER OUR CONTRACT WITH YOU TO SOMEONE ELSE
            </h3>

            <ul className="list-none pl-10 mt-5">
              We may transfer our rights and obligations under this contract to
              another organisation. If you have an open account or subscription
              with us then we will always tell you in writing if this happens
              and if you are a consumer we will ensure that the transfer will
              not affect your rights under the contract.
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              9. YOU MAY ONLY TRANSFER YOUR CONTRACT WITH US TO SOMEONE ELSE IF
              WE AGREE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We may not agree to this. If we do, we can require the party you
                transfer the contract to to prove that you transferred the
                Subscription and all account access to them. If you are a
                business you need our agreement to transfer your contract with
                us and it is entirely up to us whether we give it.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              10. OUR SITE IS ONLY FOR USERS IN THE UK
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                10.1 Our site is directed to people residing in the United
                Kingdom. We do not represent that content and services available
                on or through our site are appropriate for use or available in
                other locations.
              </li>
              <li className="mt-2">
                10.2 By continuing to access, view or make use of this site and
                any related content and services, you hereby warrant and
                represent to us that you are located in the United Kingdom. If
                you are not located in United Kingdom, you must immediately
                discontinue use of this site and any related content and
                services.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              11. YOU MUST KEEP YOUR ACCOUNT DETAILS SAFE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                11.1 If you choose, or you are provided with, a user
                identification code, password or any other piece of information
                as part of our security or account sign in procedures, you must
                treat such information as confidential.
              </li>
              <li className="mt-2">
                11.2 You must not disclose it to any third party.
              </li>
              <li className="mt-2">
                11.3 We have the right to disable any user identification code
                or password, whether chosen by you or allocated by us, and to
                close your account at any time, if in our reasonable opinion you
                have failed to comply with any of the provisions of these terms.
              </li>
              <li className="mt-2">
                11.4 If you know or suspect that anyone other than you knows
                your user identification code or password, you must promptly
                notify us.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              12. HOW YOU MAY USE MATERIAL ON OUR SITE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                12.1 We are the owner or the licensee of all intellectual
                property rights in our site, and in the material published on
                it. Those works are protected by copyright laws and treaties
                around the world. All such rights are reserved.
              </li>
              <li className="mt-2">
                12.2 You may print off one copy, and may download extracts, of
                any page(s) from our site for your personal use and you may draw
                the attention of others within your organisation to content
                posted on our site.
              </li>
              <li className="mt-2">
                12.3 You must not modify the paper or digital copies of any
                materials you have printed off or downloaded in any way, and you
                must not use any illustrations, photographs, video or audio
                sequences or any graphics separately from any accompanying text.
              </li>
              <li className="mt-2">
                12.4 Our status (and that of any identified contributors) as the
                authors of content on our site must always be acknowledged.
              </li>
              <li className="mt-2">
                12.5 You must not use any part of the content on our site for
                commercial purposes without obtaining a licence to do so from us
                or our licensors.
              </li>
              <li className="mt-2">
                12.6 If you print off, copy, download, share or repost any part
                of our site in breach of these terms of use, your right to use
                our site will cease immediately and you must, at our option,
                return or destroy any copies of the materials you have made.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              13. NO TEXT OR DATA MINING, OR WEB SCRAPING
            </h3>
            <ul className="pl-10 mt-5">
              <li className="mt-2">
                13.1 You shall not conduct, facilitate, authorise or permit any
                text or data mining or web scraping in relation to our site or
                any services provided via, or in relation to, our site. This
                includes using (or permitting, authorising or attempting the use
                of):
                <ul className="pl-10 mt-5">
                  <li className="mt-2">
                    13.1.1 Any “robot”, “bot”, “spider”, “scraper” or other
                    automated device, program, tool, algorithm, code, process or
                    methodology to access, obtain, copy, monitor or republish
                    any portion of the site or any data, content, information or
                    services accessed via the same.
                  </li>
                  <li className="mt-2">
                    13.1.2 Any automated analytical technique aimed at analysing
                    text and data in digital form to generate information which
                    includes but is not limited to patterns, trends and
                    correlations.
                  </li>
                </ul>
                <li className="mt-4">
                  13.2 The provisions in this clause should be treated as an
                  express reservation of our rights in this regard, including
                  for the purposes of Article 4(3) of Digital Copyright
                  Directive ((EU) 2019/790).
                </li>
                <li className="mt-2">
                  13.3 This clause shall not apply insofar as (but only to the
                  extent that) we are unable to exclude or limit text or data
                  mining or web scraping activity by contract under the laws
                  which are applicable to us.
                </li>
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              14. DO NOT RELY ON INFORMATION ON THIS SITE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                14.1 The content on our site is provided for general information
                only. It is not intended to amount to advice on which you should
                rely. You must obtain professional or specialist advice before
                taking, or refraining from, any action on the basis of the
                content on our site.
              </li>
              <li className="mt-2">
                14.2 Although we make reasonable efforts to update the
                information on our site, we make no representations, warranties
                or guarantees, whether express or implied, that the content on
                our site is accurate, complete or up to date.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              15. WE ARE NOT RESPONSIBLE FOR WEBSITES WE LINK TO
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                15.1 Where our site contains links to other sites and resources
                provided by third parties, these links are provided for your
                information only. Such links should not be interpreted as
                approval by us of those linked websites or information you may
                obtain from them.
              </li>
              <li className="mt-2">
                15.2 We have no control over the contents of those sites or
                resources.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              16. OUR RESPONSIBILITY FOR LOSS OR DAMAGE SUFFERED BY YOU
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We do not exclude or limit in any way our liability to you where
                it would be unlawful to do so. This includes liability for death
                or personal injury caused by our negligence or the negligence of
                our employees, agents or subcontractors and for fraud or
                fraudulent misrepresentation. Different limitations and
                exclusions of liability will apply to liability arising as a
                result of the supply of any products or Services to you, which
                will be set out in our Website{' '}
                <a href={'#supplyOfServices'}>
                  terms and conditions for the supply
                </a>{' '}
                of services.
              </li>
              <li className="mt-2">
                16.1 Our liability to consumers: Please note that we only
                provide our site for domestic and private use. You agree not to
                use our site for any commercial or business purposes, and we
                have no liability to you for any loss of profit, loss of
                business, business interruption, or loss of business
                opportunity. If defective digital content that we have supplied,
                damages a device or digital content belonging to you and this is
                caused by our failure to use reasonable care and skill, we will
                either repair the damage or pay you compensation. However, we
                will not be liable for damage that you could have avoided by
                following advice to apply a free update or by correctly
                following the installation instructions or having the minimum
                system requirements advised.
              </li>
              <li className="mt-2">
                16.2 Our liability to businesses: We exclude all implied
                conditions, warranties, representations or other terms that may
                apply to our site or any content on it. We will not be liable to
                you for any loss or damage, whether in contract, tort (including
                negligence), breach of statutory duty, or otherwise, even if
                foreseeable, arising under or in connection with:
                <ul className="list-none pl-10 mt-5">
                  <li>
                    16.2.1 Use of, or inability to use, our site; or use of or
                    reliance on any content displayed on our site.
                  </li>
                  <li>
                    16.2.2 In particular, we will not be liable for:
                    <ul className="list-none pl-10 mt-5">
                      <li>
                        16.2.2.1 Loss of profits, sales, business, or revenue;
                      </li>
                      <li>16.2.2.2 Business interruption;</li>
                      <li>16.2.2.3 Loss of anticipated savings;</li>
                      <li>
                        16.2.2.4 Any indirect or consequential loss or damage.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              17. HOW WE MAY USE YOUR PERSONAL INFORMATION
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We will only use your personal information as set out in our{' '}
                <a href={'/privacy-policy'}>Privacy Policy</a>.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              18. WE ARE NOT RESPONSIBLE FOR VIRUSES AND YOU MUST NOT INTRODUCE
              THEM
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                18.1 We do not guarantee that our site will be secure or free
                from bugs or viruses.
              </li>
              <li className="mt-2">
                18.2 You are responsible for configuring your information
                technology, computer programmes and platform to access our site.
                You should use your own virus protection software.
              </li>
              <li className="mt-2">
                18.3 You must not misuse our site by knowingly introducing
                viruses, trojans, worms, logic bombs or other material that is
                malicious or technologically harmful. You must not attempt to
                gain unauthorised access to our site, the server on which our
                site is stored or any server, computer or database connected to
                our site. You must not attack our site via a denial-of-service
                attack or a distributed denial-of service attack. By breaching
                this provision, you would commit a criminal offence under the
                Computer Misuse Act 1990. We will report any such breach to the
                relevant law enforcement authorities and we will co-operate with
                those authorities by disclosing your identity to them. In the
                event of such a breach, your right to use our site will cease
                immediately.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              19. RULES ABOUT LINKING TO OUR SITE
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                19.1 You may link to our home page, provided you do so in a way
                that is fair and legal and does not damage our reputation or
                take advantage of it.
              </li>
              <li className="mt-2">
                19.2 You must not establish a link in such a way as to suggest
                any form of association, approval or endorsement on our part
                where none exists.
              </li>
              <li className="mt-2">
                19.3 You must not establish a link to our site in any website
                that is not owned by you.
              </li>
              <li className="mt-2">
                19.4 Our site must not be framed on any other site, nor may you
                create a link to any part of our site other than the home page.
              </li>
              <li className="mt-2">
                19.5 We reserve the right to withdraw linking permission without
                notice.
              </li>
              <li className="mt-2">
                19.6 The website in which you are linking must comply in all
                respects with the content standards set out in our{' '}
                <a href={'#acceptancePolicy'}>Acceptable Use Policy.</a>
              </li>
              <li className="mt-2">
                19.6 The website in which you are linking must comply in all
                respects with the content standards set out in our{' '}
                <a href={'#acceptancePolicy'}>Acceptable Use Policy.</a>
              </li>
              <li>
                19.7 If you wish to link to or make any use of content on our
                site other than that set out above, please contact us using the
                contact details in section 2.2 above.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              20. OUR TRADE MARK IS REGISTERED
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2 flex flex-wrap">
                www.{HOSTNAME} is a {COMPANY_NUMBER && companyNumber} trade mark
                of {companyName}. You are not permitted to use it without our
                approval, unless it is part of material you are using as
                permitted under the section in this agreement on How you may use
                material on our site.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              21. GENERAL
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                21.1 Governing law and jurisdiction These terms are governed by
                English law. If you are a consumer then, wherever you live, you
                can bring claims against us in the English courts and if you
                live in Wales, Scotland or Northern Ireland, you can also bring
                claims against us in the courts of the country you live in. If
                you are a consumer we can claim against you in the courts of the
                country you live in. If you are a business you irrevocably agree
                to submit all disputes arising out of or in connection with our
                contract with you to the exclusive jurisdiction of the English
                courts.
              </li>
              <li className="mt-2">
                21.2 Nobody else has any rights under this contract. This
                contract is between you and us. Nobody else can enforce it and
                neither of us will need to ask anybody else to sign-off on
                ending or changing it.
              </li>
              <li className="mt-2">
                21.3 If a court invalidates some of this contract, the rest of
                it will still apply. If a court or other authority decides that
                some of these terms are unlawful, the rest will continue to
                apply.
              </li>
              <li className="mt-2">
                21.4 Even if we delay in enforcing this contract, we can still
                enforce it later. We might not immediately chase you for not
                doing something (like paying) or for doing something you’re not
                allowed to, but that doesn’t mean we can’t do it later.
              </li>
            </ul>

            <h1
              className="text-3xl text-center leading-8 font-bold tracking-tight text-gray-900 my-10 sm:text-4xl sm:leading-10 py-10"
              id="acceptancePolicy"
            >
              Acceptable Use Policy
            </h1>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              1. ABOUT US
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2 flex flex-wrap">
                1.1 Company details: {companyName} (company number{' '}
                {companyNumber}) (we and us) is a company registered in England
                and Wales and our registered office is at{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                . {companyName} also trades under the trading name(s) {HOSTNAME}
                . Our main trading address is{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                .
              </li>
              <li className="mt-2 flex flex-wrap">
                1.2 Contacting us: To contact us by email at contact@{HOSTNAME}.
                You may also contact us in writing at{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                .
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              2. BY USING OUR SITE YOU ACCEPT THESE TERMS
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                By using our site, you confirm that you accept these terms and
                that you agree to comply with them. You are also responsible for
                ensuring that all persons who access our site through your
                internet connection are aware of these terms and other
                applicable terms and conditions, and that they comply with them.
                If you do not agree to these terms, you must not use our site.
                We recommend that you print a copy of these terms for future
                reference.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              3. THERE ARE OTHER TERMS THAT MAY APPLY TO YOU
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                3.1 These terms of use refer to the following additional terms,
                which also apply to your use of our site:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.1.1 Our Website{' '}
                    <a href={'#terms'}>terms and conditions </a>
                    apply to your use of our site.
                  </li>
                  <li className="mt-2">
                    3.1.2 Our <a href={'/privacy-policy'}>Privacy Policy</a>.
                    See further under{' '}
                    <a href={'/privacy-policy'}>
                      How we may use your personal{' '}
                    </a>
                    information.
                  </li>
                  <li className="mt-2">
                    3.1.3 Our Cookie Policy, which sets out information about
                    the cookies on our site.
                  </li>
                  <li className="mt-2">
                    3.1.4 If you purchase services from our site, our Website{' '}
                    <a href={'#supplyOfServices'}>
                      terms and conditions of supply
                    </a>
                    will apply to your purchase.
                  </li>
                </ul>
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              4. WE MAY MAKE CHANGES TO THESE TERMS
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We amend these terms from time to time. Every time you wish to
                use our site, please check these terms to ensure you understand
                the terms that apply at that time. These terms were most
                recently updated on 18/07/2022 when we changed clauses 20 and
                21.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              5. PROHIBITED USES
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">5.1 You may not use our site:</li>
              <ul className="list-none pl-10 mt-5">
                <li className="mt-2">
                  5.1.1 In any way that breaches any applicable local, national
                  or international law or regulation.
                </li>

                <li className="mt-2">
                  5.1.2 In any way that is unlawful or fraudulent or has any
                  unlawful or fraudulent purpose or effect.
                </li>

                <li className="mt-2">
                  5.1.3 For the purpose of harming or attempting to harm minors
                  in any way.
                </li>

                <li className="mt-2">
                  5.1.4 To bully, insult, intimidate or humiliate any person.
                </li>
                <li className="mt-2">
                  5.1.5 To send, knowingly receive, upload, download, use or
                  re-use any material which does not comply with our content
                  standards.
                </li>

                <li className="mt-2">
                  5.1.6 To transmit, or procure the sending of, any unsolicited
                  or unauthorised advertising or promotional material or any
                  other form of similar solicitation (spam).
                </li>

                <li className="mt-2">
                  5.1.7 To knowingly transmit any data, send or upload any
                  material that contains viruses, Trojan horses, worms,
                  time-bombs, keystroke loggers, spyware, adware or any other
                  harmful programs or similar computer code designed to
                  adversely affect the operation of any computer software or
                  hardware.
                </li>

                <li className="mt-2">5.1.8 To upload terrorist content.</li>
              </ul>
              <li className="mt-2">5.2 You also agree:</li>
              <ul className="list-none pl-10 mt-5">
                <li className="mt-2">
                  5.2.1 Not to reproduce, duplicate, copy or re-sell any part of
                  our site in contravention of the provisions of our Website{' '}
                  <a href={'#terms'}>terms and conditions </a>
                  and Website
                  <a href={'#supplyOfServices'}>
                    terms and conditions for the supply of services
                  </a>
                </li>

                <li className="mt-2">
                  5.2.2 Not to access without authority, interfere with, damage
                  or disrupt any part of our site; any equipment or network on
                  which our site is stored; any software used in the provision
                  of our site; or any equipment or network or software owned or
                  used by any third party.
                </li>
              </ul>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              6. CONTENT STANDARDS
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                6.1 These content standards apply to any and all material which
                you contribute to our site (Contribution).
              </li>
              <li className="mt-2">
                6.2 The Content Standards must be complied with in spirit as
                well as to the letter. The standards apply to each part of any
                Contribution as well as to its whole.
              </li>
              <li className="mt-2 flex flex-wrap">
                6.3 {companyName} will determine, in its sole discretion,
                whether a Contribution breaches the Content Standards.
              </li>
              <li className="mt-2 flex flex-wrap">6.4 A Contribution must:</li>
              <ul className="list-none pl-10 mt-5">
                <li className="mt-2">
                  6.4.1 Be accurate (where it states facts).
                </li>

                <li className="mt-2">
                  6.4.2 Be genuinely held (where it states opinions).
                </li>

                <li className="mt-2">
                  6.4.3 Comply with the law applicable in England and Wales and
                  in any country from which it is posted.
                </li>
              </ul>
              <li className="mt-2">
                6.5 A Contribution must not:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    6.5.1 Be accurate (where it states facts).
                  </li>

                  <li className="mt-2">
                    6.5.2 Be genuinely held (where it states opinions).
                  </li>

                  <li className="mt-2">
                    6.5.3 Comply with the law applicable in England and Wales
                    and in any country from which it is posted.
                  </li>
                  <li className="mt-2"> 6.5.4 Be defamatory of any person.</li>
                  <li className="mt-2">
                    6.5.5 Be obscene, offensive, hateful or inflammatory.
                  </li>
                  <li className="mt-2">
                    6.5.6 Bully, insult, intimidate or humiliate.
                  </li>
                  <li className="mt-2">
                    6.5.7 Promote sexually explicit material.
                  </li>
                  <li className="mt-2">
                    6.5.8 Include child sexual abuse material.
                  </li>
                  <li>6.5.9 Promote violence.</li>
                  <li className="mt-2">
                    6.5.10 Promote discrimination based on race, sex, religion,
                    nationality, disability, sexual orientation or age.
                  </li>
                  <li className="mt-2">
                    6.5.11 Infringe any copyright, database right or trade mark
                    of any other person.
                  </li>
                  <li className="mt-2">
                    6.5.12 Be likely to deceive any person.
                  </li>
                  <li className="mt-2">
                    6.5.13 Breach any legal duty owed to a third party, such as
                    a contractual duty or a duty of confidence.
                  </li>
                  <li className="mt-2">
                    6.5.14 Promote any illegal content or activity.
                  </li>
                  <li className="mt-2">6.5.15 Be in contempt of court.</li>
                  <li className="mt-2">
                    6.5.16 Be threatening, abuse or invade another&apos;s
                    privacy, or cause annoyance, inconvenience or needless
                    anxiety.
                  </li>
                  <li className="mt-2">
                    6.5.17 Be likely to harass, upset, embarrass, alarm or annoy
                    any other person.
                  </li>
                  <li className="mt-2">
                    6.5.18 Impersonate any person or misrepresent your identity
                    or affiliation with any person.
                  </li>
                  <li className="mt-2 flex flex-wrap">
                    6.5.19 Give the impression that the Contribution emanates
                    from {companyName}, if this is not the case.
                  </li>
                  <li className="mt-2">
                    6.5.20 Advocate, promote, incite any party to commit, or
                    assist any unlawful or criminal act such as (by way of
                    example only) copyright infringement or computer misuse.
                  </li>
                  <li className="mt-2">
                    6.5.21 Contain a statement which you know or believe, or
                    have reasonable grounds for believing, that members of the
                    public to whom the statement is, or is to be, published are
                    likely to understand as a direct or indirect encouragement
                    or other inducement to the commission, preparation or
                    instigation of acts of terrorism.
                  </li>
                </ul>
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              7. BREACH OF THIS POLICY
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                7.1 When we consider that a breach of this acceptable use policy
                has occurred, we may take such action as we deem appropriate.
              </li>
              <li className="mt-2">
                7.2 Failure to comply with this acceptable use policy
                constitutes a material breach of our Website{' '}
                <a href={'#terms'}>terms and conditions </a>
                upon which you are permitted to use our site, and may result in
                our taking all or any of the following actions:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    7.2.1 Immediate, temporary or permanent withdrawal of your
                    right to use our site.
                  </li>

                  <li className="mt-2">
                    7.2.2 Immediate, temporary or permanent removal of any
                    Contribution uploaded by you to our site.
                  </li>

                  <li className="mt-2">7.2.3 Issue of a warning to you.</li>
                  <li className="mt-2">
                    7.2.4 Legal proceedings against you for reimbursement of all
                    costs on an indemnity basis (including, but not limited to,
                    reasonable administrative and legal costs) resulting from
                    the breach.
                  </li>

                  <li className="mt-2">
                    7.2.5 Further legal action against you.
                  </li>
                  <li className="mt-2">
                    7.2.6 Disclosure of such information to law enforcement
                    authorities as we reasonably feel is necessary or as
                    required by law.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                7.3 We exclude our liability for all action we may take in
                response to breaches of this acceptable use policy. The actions
                we may take are not limited to those described above, and we may
                take any other action we reasonably deem appropriate.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              8. HOW THIS CONTRACT CAN BE TRANSFERRED
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                We can transfer our rights and obligations under these terms to
                any third party, provided this does not adversely affect your
                rights under these terms.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              9. GENERAL
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                9.1 Governing law and jurisdiction
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    9.1.1 These terms are governed by English law.
                  </li>

                  <li className="mt-2">
                    9.1.2 If you are a consumer then, wherever you live, you can
                    bring claims against us in the English courts and if you
                    live in Wales, Scotland or Northern Ireland, you can also
                    bring claims against us in the courts of the country you
                    live in. If you are a consumer we can claim against you in
                    the courts of the country you live in.
                  </li>

                  <li className="mt-2">
                    9.1.3 If you are a business you irrevocably agree to submit
                    all disputes arising out of or in connection with our
                    contract with you to the exclusive jurisdiction of the
                    English courts.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                9.2 If a court invalidates some of this contract, the rest of it
                will still apply. If a court or other authority decides that
                some of these terms are unlawful, the rest will continue to
                apply.
              </li>
              <li className="mt-2">
                9.3 Even if we delay in enforcing this contract, we can still
                enforce it later. We might not immediately chase you for not
                doing something (like paying) or for doing something you’re not
                allowed to, but that doesn’t mean we can’t do it later.
              </li>
            </ul>

            <h1
              className="text-3xl text-center leading-8 font-bold tracking-tight text-gray-900 my-10 sm:text-4xl sm:leading-10 py-10"
              id="acceptancePolicy"
            >
              Terms and Conditions for the Supply of Services
            </h1>

            <p>
              YOUR ATTENTION IS PARTICULARLY DRAWN TO THE PROVISIONS OF SECTION
              3 (YOUR SUBSCRIPTION) AND SECTION 8 (LIMITATION OF LIABILITY) OF
              THESE TERMS. SPECIAL TERMS APPLY TO BUSINESS CUSTOMERS. SEE
              SECTION 10 FOR FURTHER DETAILS. THESE TERMS MAY HAVE CHANGED SINCE
              YOU LAST REVIEWED THEM. THESE TERMS WERE LAST UPDATED ON
              18/07/2022.
            </p>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              1. ABOUT US
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2 flex flex-wrap">
                1.1 Company details: {companyName} (company number{' '}
                {companyNumber}) (we and us) is a company registered in England
                and Wales and our registered office is at{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                . {companyName} also trades under the trading name(s) {HOSTNAME}
                . Our main trading address is{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                . We operate the website {HOSTNAME}.
              </li>
              <li className="mt-2 flex flex-wrap" id="section1.2">
                1.2 Contacting us: To contact us, email us at contact@{HOSTNAME}
                . You may also contact us in writing at{' '}
                {isEstonianAddress ? (
                  estoniaAddress
                ) : (
                  <>
                    England and Wales and our registered office is at{' '}
                    {COMPANY_ADDRESS}
                  </>
                )}
                . Details of how to give us formal notice of any matter under
                the Contract are set out in <a href={'#section9'}>section 9</a>.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              2. OUR CONTRACT WITH YOU
            </h3>

            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                2.1 Our contract: These terms and conditions (Terms) apply to
                all transactions between you and us for subscriptions, free
                trials and the use of our Services, whether these require a
                payment from you or not. You must be a resident of the United
                Kingdom, at least eighteen (18) years old and have legal
                capacity to enter into a contract with us.
              </li>
              <li className="mt-2">
                2.2 Your personal data: How we use any personal data you give us
                is set out in our <a href={'/privacy'}>Privacy Policy</a>.
              </li>
              <li className="mt-2">
                2.3 Your personal data: These Terms and the contract are made
                only in the English language.
              </li>

              <li className="mt-2">
                2.4 Your copy: You should print off a copy of these Terms or
                save them to your computer for future reference.
              </li>

              <li className="mt-2">
                2.5 Our right to make changes to these Terms We may update or
                modify these Terms or any term of your contract with us and the
                terms of any referenced policies at any time where:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    2.5.1 it is necessary to reflect changes in relevant laws
                    and regulatory requirements;
                  </li>

                  <li className="mt-2">2.5.2 to correct any errors;</li>

                  <li className="mt-2">
                    2.5.3 to make our contract terms clearer;
                  </li>

                  <li className="mt-2">
                    2.5.4 to improve the functionality or range of Services we
                    offer;{' '}
                  </li>

                  <li className="mt-2">
                    2.5.6 to make technical adjustments and improvements; and
                    for any other reason.
                  </li>
                </ul>
              </li>

              <li className="mt-2">
                2.6 We may also change the Subscription Fee payable by you to us
                for your subscription and the frequency of payments we will
                automatically take from your payment card for our Services. See{' '}
                <a href={'#section3'}>section 3 </a>
                of these Terms for further information.
              </li>

              <li className="mt-2">
                2.7 We may make changes to these Terms which are immaterial or
                advantageous to you immediately, without notice to you and
                without offering cancellation, or account closure options and/or
                any refund to you.
              </li>

              <li className="mt-2">
                2.8 If we make material changes to these Terms or to our
                contract which may be significantly detrimental to you
                (Detrimental Changes), we will provide you with at least
                fourteen (14) days’ notice of this by email to the email address
                registered to your account.
              </li>

              <li className="mt-2">
                2.9 If the Detrimental Changes will take effect during a
                Subscription you have already paid a Subscription Fee for or
                within any free trial we provide to you then, when notifying you
                of such Detrimental Changes, we will offer you the option to
                cancel your Subscription or free trial and to close your account
                and receive a pro-rata refund of any Subscription Fee you have
                paid to us for the remaining period of any Subscription you
                have. If you wish to accept the cancellation offer then you must
                do so within 14 days of us making it and you will then lose
                access to our Services and your account immediately. Any refund
                due will be made by the method you used for your payment of the
                Subscription Fee within 14 days of you accepting the
                cancellation offer. If you do not accept the cancellation offer
                within 14 days then you will be bound by the changes including
                any Detrimental Changes.
              </li>

              <li className="mt-2">
                2.10 If the Detrimental Changes will apply from the renewal of
                your Subscription or the expiry of any free trial we provide to
                you then they will take effect when your Subscription starts or
                renews on your Automatic Subscription Billing Date, unless you
                disable automatic renewal of your Subscription prior to your
                Automatic Subscription Billing Date (
                <a href={'#section3.6'}>see section 3.6</a>) in which case you
                will no longer have access to our Services.
              </li>

              <li className="mt-2">
                2.11 We may end our contract with you: We can end our contract
                with you and claim any compensation due to us if you don’t make
                any payment to us when it’s due; or if you don’t, within a
                reasonable time of us asking for it, provide us with information
                or cooperation that we need in order to provide a Service to
                you. If we do so then all data within your account including any
                digital content will be subject to deletion without warning.
              </li>

              <li className="mt-2">
                2.12 We may transfer our contract with you to someone else: We
                may transfer our rights and obligations under this contract to
                another organisation. We will always tell you in writing if this
                happens and if you are a consumer we will ensure that the
                transfer will not affect your rights under the contract.
              </li>

              <li className="mt-2">
                2.13 You may only transfer your contract with us to someone else
                if we agree: We may not agree to this. If we do, we can require
                the party you transfer the contract to to prove that you
                transferred the subscription and your account access to them. If
                you are a business you need our agreement to transfer your
                contract with us and it is entirely up to us whether we give it.
              </li>
            </ul>

            <h3 className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10">
              3. YOUR SUBSCRIPTION
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                3.1 Your Subscription, Subscription Fee and Automatic
                Subscription Billing Date
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.1.1 We provide our Services (see{' '}
                    <a href={'#section4'}>section 4 </a>
                    below) on a subscription basis and we take payments at
                    regular intervals from you in order to provide our Services
                    to you.
                  </li>
                  <li className="mt-2">
                    3.1.2 Your Subscription is the length (Subscription
                    Duration) that is stated on our website at the time that
                    your subscription commences or as otherwise advised by us to
                    you when your Subscription is due to renew. It starts at the
                    time that you commence your paid Subscription or, for
                    renewals, each time that your Subscription is renewed on
                    your Automatic Subscription Billing date, and it ends when
                    the Subscription Duration lapses.
                  </li>
                  <li className="mt-2">
                    3.1.3 In the event that the numeric day that your
                    Subscription starts is not contained in the month in which
                    your Subscription is due to lapse then your Subscription
                    will end on the last day of the month in which your
                    Subscription would otherwise lapse. By way of example only,
                    if your Subscription is for a Subscription Duration of 3
                    months beginning on 31 January then it will end on 30 April
                    and, if automatic renewal is not disabled prior to 30 April,
                    your Subscription will be renewed and will begin again on 30
                    April for a further Subscription Duration of 3 months to end
                    on 30 July.
                  </li>
                  <li className="mt-2">
                    3.1.4 Your Subscription Fee is the amount payable by you to
                    us in advance for your Subscription, as stated on our
                    website at the time you commenced your subscription or as
                    otherwise advised by us to you. It includes any VAT and/or
                    any applicable taxes.{' '}
                  </li>
                  <li className="mt-2">
                    3.5 By subscribing to our Services you agree to pay the
                    Subscription Fee on a recurring basis each time your
                    Subscription renews and for an indefinite period of time,
                    until your Subscription is either cancelled by you or us in
                    accordance with these Terms.
                  </li>
                  <li className="mt-2">
                    3.1.6 Your Automatic Subscription Billing Date is the date
                    that your Subscription ends, or that your free trial ends if
                    we have offered a free trial to you.
                  </li>
                  <li className="mt-2">
                    3.1.7 Your Subscription will be renewed by us and start
                    again automatically for the Subscription Duration on your
                    Automatic Subscription Billing Date and you will be charged
                    the Subscription Fee on that date in accordance with{' '}
                    <a href={'#section3.3'}>section 3.3</a>, unless you have
                    disabled automatic renewal prior to the Automatic
                    Subscription Billing Date in accordance with{' '}
                    <a href={'#section3.6'}>section 3.6</a>.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                3.2 We may vary the Subscription Fee and Subscription Duration:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.2.1 We may vary the Subscription Fee and/or Subscription
                    Duration at any time, although any such variation will only
                    apply to you as of your Automatic Subscription Billing Date
                    when your Subscription will automatically be renewed. We
                    will provide you with at least fourteen (14) days’ notice of
                    any variation to your Subscription Fee and/or Subscription
                    Duration by email to the email address registered to your
                    account.
                  </li>
                  <li className="mt-2">
                    3.2.2 If you do not wish to accept any variation we make to
                    your Subscription Fee and/or Subscription Duration then you
                    must disable automatic renewal of your subscription in
                    accordance with <a href={'#section3.6'}>section 3.6 </a>
                    of these Terms before the Automatic Subscription Billing
                    Date.
                  </li>
                  <li className="mt-2">
                    3.2.3 Note that if the rate of VAT changes any time before
                    the Subscription Fee is charged to you then we will adjust
                    the rate of VAT that you pay.
                  </li>
                  <li className="mt-2">
                    3.2.4 Occasionally there may be an error or an inaccuracy in
                    the price or description of our Subscription Fee and/or
                    Subscription Duration. If this happens then we will contact
                    you by email to the email address registered in your account
                    to establish whether you want to cancel your Subscription at
                    no cost and obtain a refund of any payment that you have
                    made for the relevant Subscription; or proceed with your
                    Subscription based on the corrected price or description.
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section3.3">
                3.3 We will automatically renew your Subscription:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.3.1 Your Subscription will automatically renew on the
                    Automatic Subscription Billing Date for the Subscription
                    Duration and on each Automatic Subscription Billing Date
                    thereafter and may be renewed by us even if we are unable to
                    take payment for your Subscription Fee from you until a
                    later date.
                  </li>

                  <li className="mt-2">
                    3.3.2 You may disable the automatic renewal of your
                    Subscription at any time by following the instructions in{' '}
                    <a href={'#section3.6'}>section 3.6 </a>
                    below. If you disable automatic renewal then any use you
                    have of our Services will cease upon the expiry of your
                    Subscription or on the expiry of your free trial if we
                    provided one to you.
                  </li>
                  <li className="mt-2">
                    3.3.3 If the payment information you provide cannot be
                    verified, is invalid or is otherwise not acceptable, or if
                    payment cannot be taken at the required time, then we may
                    make one or more further attempts to collect your
                    Subscription Fee from you using your payment information
                    during the thirty (30) days that follow your Automatic
                    Subscription Billing Date. However, your Subscription may be
                    suspended or cancelled by us and/or your account may be
                    closed without notice if payment cannot be taken at the
                    required time and whether or not we have made any further
                    attempts to collect your Subscription Fee from you. If this
                    occurs then all data within your account including any
                    digital content will be subject to deletion without warning.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                3.4 Your payment information:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.4.1 To access our Services, you will need to create an
                    online account with us and provide your payment information.
                    We currently accept Visa and Mastercard credit and debit
                    payment cards.
                  </li>
                  <li className="mt-2">
                    3.4.2 In accordance with our Privacy Policy, we will retain
                    your payment information when you provide this to us. This
                    information is used to take payment for your Subscription
                    from your payment card on or following each Automatic
                    Subscription Billing Date (see{' '}
                    <a href={'#section3.3'}>sections 3.3 </a>
                    and <a href={'#section3.5'}>sections 3.5 </a>
                    ), to pay any Government Fee payable by you for any Service
                    that we provide to you (see{' '}
                    <a href={'#section4.7'}>sections 4.7 </a>
                    ), and so that we may suggest its use for any subsequent
                    transaction you enter into with us, so that you do not need
                    to re-enter your payment information each time you transact.
                  </li>
                  <li className="mt-2">
                    3.4.3 We use third parties to process payments. You agree we
                    may securely transfer your information including your
                    payment information to those third parties for the purpose
                    of processing your payments. On occasion we may also obtain
                    and use updated credit/debit card account information from
                    credit/debit card providers in order to retry payments that
                    have failed and to complete transactions, including by
                    reattempting failed payments with extended expiration dates.
                    Please contact your credit/debit card issuer if you require
                    more information about this service.
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section3.5">
                3.5 You will be charged the Subscription Fee after a free trial
                if you do not disable automatic renewal:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.5.1 We may, at our sole discretion, offer you a free trial
                    of our Services. If we do so then we will retain your
                    payment information in accordance with and for the purposes
                    set out in <a href={'#section3.3'}>sections 3.3 </a>
                    and <a href={'#section3.4'}>sections 3.4 </a>
                    above.
                  </li>
                  <li className="mt-2">
                    3.5.2 Any free trial offered will expire in accordance with
                    the expiry date advised by us to you when you accepted the
                    trial.
                  </li>
                  <li className="mt-2">
                    3.5.3 Your Automatic Subscription Billing Date will be the
                    same date that your free trial expires. If you do not cancel
                    your free trial or disable automatic renewal in accordance
                    with <a href={'#section3.6'}>section 3.6 </a>
                    below prior to the Automatic Subscription Billing Date then
                    we will use your payment information to charge you the
                    Subscription Fee on that date and then to charge you the
                    Subscription Fee on each date that your Subscription lapses
                    thereafter, in accordance with{' '}
                    <a href={'#section3.3'}>section 3.3 </a>. Your Subscription
                    will begin on the Automatic Subscription Billing Date and
                    will be for the Subscription Duration as advised by us to
                    you when you commenced your free trial or as advised by us
                    to you from time to time.
                  </li>
                  <li className="mt-2">
                    3.5.4 You may disable the automatic renewal of your
                    Subscription at any time by following the instructions in{' '}
                    <a href={'#section3.6'}>section 3.6 </a>
                    below.
                  </li>
                  <li className="mt-2">
                    3.5.5 Our free trials are intended for new customers and
                    certain former customers to try out our Services.
                    Eligibility is determined solely by us and we may limit
                    access to free trials or the duration of free trials to
                    prevent abuse.
                  </li>
                  <li className="mt-2">
                    3.5.6 We can terminate a free trial without notice and for
                    any reason at any time including if we determine that you
                    are not eligible for the trial.
                  </li>
                  <li className="mt-2">
                    3.5.7 If your free trial is terminated by us then we will
                    not charge you the Subscription Fee, you will not have a
                    Subscription with us and we will have no liability to you.
                    Upon termination of a free trial by us, you will no longer
                    have access to our Services. If this occurs then all data
                    within your account including any digital content will be
                    subject to deletion without warning.
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section3.6">
                3.6 Disabling automatic renewal:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.6.1 You can disable automatic renewal of your Subscription
                    at any time during the Subscription or free trial provided
                    that you do so no later than the day before your Automatic
                    Subscription Billing Date or the day before your free trial
                    expires. You can do so by calling us using the telephone
                    number provided in our{' '}
                    <a href={'/contacts'}>Contact Details </a>
                    section or by signing in to your account and then clicking
                    ‘My Subscription’, then ‘Disable automatic renewal’, and
                    then following the onscreen prompts.
                  </li>
                  <li className="mt-2">
                    3.6.2 If you disable automatic renewal in accordance with
                    the above paragraph then your access to our Services will
                    cease upon the expiry of your current Subscription or free
                    trial and we will not charge you for a further Subscription
                    when your Subscription or free trial ends. However, you will
                    continue to have access to our Services until the end of any
                    already paid Subscription or free trial.
                  </li>
                  <li className="mt-2">
                    3.6.3 If you have any questions, please contact us using the
                    details in <a href={'#section1.2'}>section 1.2 </a>
                    above.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                3.7 Your use of our Services is personal to you:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.7.1 We will grant you a non-exclusive, non-transferrable
                    right and licence, without the right to grant sublicences,
                    to use our Services during any free trial we provide to you
                    and during any Subscription for which you have paid the
                    Subscription Fee.
                  </li>
                  <li className="mt-2">
                    3.7.2 Only you are authorised to access and use your
                    account, your Subscription and our Services or any free
                    trial we provide to you. Our Services and any products,
                    goods or digital content we provide to you are personal to
                    you.
                  </li>
                  <li className="mt-2">
                    3.7.3 You agree not to disclose and to keep secure your
                    account password and you agree that you will not allow your
                    account or Subscription or any free trial we provide to you
                    to be used by any other person.
                  </li>
                  <li className="mt-2">
                    3.7.4 You agree not to access all or any part of the
                    Services in order to build a product or service which
                    competes with our Services or to use your Subscription or
                    our Services to provide services to others.
                  </li>
                  <li className="mt-2">
                    3.7.5 You agree not to license, sell, rent, lease, transfer,
                    assign, distribute, display, disclose, or otherwise
                    commercially exploit or make our Services or any products,
                    goods or digital content we provide to you available to any
                    third party.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                3.8 You must keep your account and Subscription details safe:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.8.1 You agree to use all reasonable endeavours to prevent
                    any unauthorised access to or use of your account and your
                    Subscription and agree, in the event of any such
                    unauthorised access or use, to promptly notify us.
                  </li>
                  <li className="mt-2">
                    3.8.2 We have the right to disable any user identification
                    code or password, whether chosen by you or allocated by us,
                    and to close your account at any time, if in our reasonable
                    opinion you have failed to comply with any of the provisions
                    of these Terms.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                3.9 We may not renew your Subscription or may cancel your
                Subscription or your free trial at any time:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    3.9.1 We reserve the right at our absolute discretion not to
                    renew your Subscription or to cancel your Subscription or
                    your free trial at any time and without giving any reasons
                    for our decision to do so. If we do then we will make a
                    pro-rata refund of any Subscription Fee you have paid to us
                    for any days remaining in any Subscription you have with us
                    and any Government Fees you have paid for any Services we
                    have not yet provided to you.
                  </li>
                  <li className="mt-2">
                    3.9.2 We may also cancel or restrict your access to and use
                    of our Services and/or we may close your account without
                    notice. If your account is closed, all data within it
                    including any digital content will be subject to deletion
                    without warning.
                  </li>
                </ul>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section4"
            >
              4. SERVICES
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                4.1 Services we provide and our timescales for doing so:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.1.1 For consumers and business users, our Services are
                    driving licence renewals, administrative support, basic
                    legal assistance and a loyalty programme as described on our
                    website. Each of these is individually a Service.
                  </li>
                  <li className="mt-2">
                    4.1.2 Access to your Subscription and/or our Services will
                    generally be provided upon your payment of the Subscription
                    Fee or the commencement of a free trial with us.
                  </li>
                  <li className="mt-2">
                    4.1.3 Some of our Services, such as our loyalty programme,
                    are simply directory services and no timescale for delivery
                    applies to these on the basis that no delivery or
                    performance is required of or provided by us in respect of
                    these.
                  </li>
                  <li className="mt-2">
                    4.1.4 For our other Services, generally a timescale of 5
                    working days applies to our performance of each Service, as
                    follows:
                    <ul className="list-none pl-10 mt-5">
                      <li className="mt-2">
                        4.1.4.1 Where the Service we are providing is an
                        application for a Driving Licence Renewal, performance
                        constitutes the submitting of the application on your
                        behalf by us to the relevant government agency, the
                        Driver and Vehicle Licensing Agency (DVLA), and this
                        submission will generally be completed by us within 5
                        working days. The DVLA will then require additional time
                        to fulfil the application we have submitted; however, we
                        cannot confirm, have no control over and are not
                        responsible for the amount of time that the DVLA will
                        require to do so. Our performance of this Service is
                        considered complete and our obligations fulfilled upon
                        our submitting of the application to the DVLA on your
                        behalf for the DVLA to
                      </li>
                      <li className="mt-2">
                        4.1.4.2 Where the Service we are providing is basic
                        legal support, performance constitutes the submitting of
                        your enquiry on your behalf by us and this will
                        generally be completed by us within 5 working days. Our
                        legal provider will then require additional time to
                        consider your enquiry and to respond to you as
                        appropriate; however, we cannot confirm, have no control
                        over and are not responsible for the amount of time that
                        our legal provider will require to do so. Our
                        performance of this service is considered complete and
                        our obligations fulfilled upon our submitting of your
                        enquiry to our legal provider on your behalf for them to
                        process. Please refer to{' '}
                        <a href={'#section4.8'}>section 4.8 </a>
                        for further terms related to this Service.
                      </li>
                      <li className="mt-2">
                        4.1.4.3 We will use reasonable endeavours to meet any
                        performance dates and timescales specified by us, but
                        any such dates and timescales are estimates only and any
                        failure by us to perform a Service by such dates or
                        within such timescales will not give you the right to
                        terminate the contract or any right to compensation or
                        damages from us.
                      </li>
                      <li className="mt-2">
                        4.1.4.4 You agree that time is not of the essence under
                        our contract.
                      </li>
                      <li className="mt-2">
                        4.1.4.5 Please note that we are not affiliated with our
                        legal provider, the DVLA, the UK Government or any other
                        official body.
                      </li>
                      <li className="mt-2">
                        4.1.4.6 We are a third-party company offering a turnkey
                        package of Services including complete online validation
                        and submission services for driving licence
                        applications. Use of the Services we provide is not
                        mandatory. Applying for a driving licence from the
                        official government agency portal, which is the official
                        DVLA website and with which we are not affiliated, may
                        be lower cost or free of charge. The Subscription Fee we
                        charge is additional to any DVLA or other government
                        fees (see <a href={'#section4.7'}>section 4.7</a>
                        ).
                      </li>
                      <li className="mt-2">
                        4.1.4.7 We are not responsible for any delays or
                        failures of performance by third parties including our
                        legal provider or the DVLA.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                4.2 How to get further information about our Services:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.2.1 Should you require further information about any of
                    our Services then you may request this from us using our
                    contact details as set out in{' '}
                    <a href={'#section1.2'}>section 1.2 </a>
                    above.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                4.3 Placing an order for a Service:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.3.1 Please follow the onscreen prompts on our website to
                    place your order for one of our Services when subscribing or
                    when signed in to your account with a free trial or with an
                    active paid-for Subscription. You may only submit an order
                    using the methods set out on our website, and Government
                    Fees may be payable by you for each order you submit (see{' '}
                    <a href={'#section4.7'}>section 4.7</a>) whether or not you
                    submit your order(s) during a free trial or during an active
                    paid-for subscription.
                  </li>
                  <li className="mt-2">
                    4.3.2 Our order process allows you to check and amend any
                    errors before submitting your order to us. Please check the
                    order carefully before confirming it. You are responsible
                    for ensuring that your order and any information submitted
                    by you is complete and accurate. above.
                  </li>
                  <li className="mt-2">
                    4.3.3 By placing an order for a Service you confirm that all
                    personal information you provide is to your knowledge
                    accurate and up to date and you confirm that you are
                    eligible to access and apply for the Service, search,
                    product, goods or digital content you are applying for and
                    that you have fully complied with all applicable laws,
                    regulations and legislation.
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section4.4">
                4.4 Placing an order for a Service:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.4.1 We may be unable to accept or fulfil your order for a
                    Service. This might be because the official government
                    website we use to provide the Service to you is not
                    available for us to complete your order, because of
                    unexpected limits on our resources which we could not
                    reasonably have planned for, because you are not eligible
                    for the Service (for example because you are a consumer and
                    you have ordered a business service), because of an error in
                    our systems, because we have identified an error in the
                    price or description of the Service or for any other reason.
                  </li>
                  <li className="mt-2">
                    4.4.2 If we are unable to supply you with a Service you have
                    ordered for any reason we will inform you of this by email
                    to the email address registered to your account, we will not
                    process your order for that Service and we will refund you
                    any Government Fee you have paid to us for that Service.
                    Subject to the following paragraph, it may be possible for
                    you to place a further order for that Service at a later
                    date within your Subscription or free trial, in which case
                    any Government Fee applicable for that Service at that later
                    date will be payable by you. above.
                  </li>
                  <li className="mt-2">
                    4.4.3 If we are permanently unable to supply you with one or
                    more of our Services and you have paid a Subscription Fee
                    for your current Subscription then we will inform you of
                    this by email to the email address registered to your
                    account and offer you the option to cancel your Subscription
                    and close your account and receive a pro-rata refund of any
                    Subscription Fee you have paid to us for any days remaining
                    in your Subscription. If you wish to accept the cancellation
                    offer then you must do so within 14 days of us making the
                    cancellation offer to you and you will then lose access to
                    our Services and your account immediately. We will make any
                    such refund by the method you used for your payment of the
                    Subscription Fee within 14 days of you accepting the
                    cancellation offer.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                4.5 Placing an order for a Service:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.5.1 We provide our Services on and subject to the terms of
                    this contract. We shall use commercially reasonable
                    endeavours to make our website and our Services available 24
                    hours a day, seven days a week, although provide no
                    guarantee that the website and/or our Services will always
                    be available or be uninterrupted.
                  </li>
                  <li className="mt-2">
                    4.5.2 If our supply of a Service is delayed by an event
                    outside our control, we will contact you as soon as possible
                    to let you know and do what we can to reduce the delay. As
                    long as we do this, we won’t compensate you for the delay,
                    but if the delay is likely to be substantial you can{' '}
                    <a href={'/contacts'}>contact us </a>
                    using the contact details in{' '}
                    <a href={'#section1.2'}>section 1.2 </a>
                    above to end the contract and receive a pro-rata refund of
                    any Subscription Fee you have paid to us for any remaining
                    days in your Subscription and of any Government Fees you
                    have paid for Services you have not yet received less any
                    reasonable costs we have already incurred.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                4.6 We may vary, suspend and withdraw any of our Services at any
                time:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    4.6.1 We can vary or amend the specification of any Service
                    if required by any applicable statutory or regulatory
                    requirement or for any reason at all. We may do so without
                    notice.
                  </li>
                  <li className="mt-2">
                    4.6.2 We can suspend and restrict the supply of any Service
                    to deal with technical problems or make technical changes,
                    to update the product to reflect changes in relevant laws
                    and regulatory requirements, to make changes to the product
                    or for any reason at all. We may do so without notice.
                  </li>
                  <li className="mt-2">
                    4.6.3 We can also suspend or restrict our Services in the
                    event we have been unable to collect payment from you for a
                    Subscription when it is due. We may do so without notice.
                  </li>
                  <li className="mt-2">
                    4.6.4 We can withdraw any Service if required by any
                    applicable statutory or regulatory requirement or for any
                    reason at all. We may do so without notice.
                  </li>
                  <li className="mt-2">
                    4.6.5 If you have paid a Subscription Fee for your current
                    Subscription and during your current Subscription we suspend
                    or restrict one or more of our Services for more than 7
                    contiguous days or we withdraw one or more of our Services
                    then we will contact you by email to the email address
                    registered to your account to offer you the option to cancel
                    your Subscription and close your account and receive a
                    pro-rata refund of any Subscription Fee you have paid to us
                    for any days remaining in your Subscription. If you wish to
                    accept the cancellation offer then you must do so within 14
                    days of us making it to you and you will then lose access to
                    our Services and your account immediately. We will make any
                    such refund by the method you used for your payment of the
                    Subscription Fee within 14 days of you accepting the offer.
                  </li>
                  <li className="mt-2">
                    4.6.6 No right to any refund arises if we suspend or
                    restrict or withdraw any of our Services if you are not
                    within an active Subscription for which you have paid a
                    Subscription Fee.
                  </li>
                </ul>
                <li className="mt-2" id="section4.7">
                  4.7 Placing an order for a Service:
                  <ul className="list-none pl-10 mt-5">
                    <li className="mt-2">
                      4.7.1 A Government Fee may be payable in addition to your
                      Subscription Fee for certain of our Services. Government
                      Fees are set by the UK Government and are subject to
                      change without notice.
                    </li>
                    <li className="mt-2">
                      4.7.2 We will advise you of any such applicable fees each
                      time that you make use any of our Services. We will
                      collect the payment from you for any Government Fee due
                      using your retained payment information.
                    </li>
                    <li className="mt-2">
                      4.7.3 Government Fees are not refundable other than in the
                      event we are unable to accept or fulfil your order for a
                      Service, as detailed further in{' '}
                      <a href={'#section4.4'}>section 4.4</a>.
                    </li>
                  </ul>
                </li>
                <li className="mt-2" id="section4.8">
                  4.8 Basic Legal Assistance Service:
                  <ul className="list-none pl-10 mt-5">
                    <li className="mt-2">
                      4.8.1 A The Basic Legal Assistance service made available
                      via our website is provided by a third party.
                    </li>
                    <li className="mt-2">
                      4.8.2 If you use the Basic Legal Assistance service then
                      you consent to our sharing of your name, contact details
                      and legal assistance enquiry with our legal provider in
                      accordance with our{' '}
                      <a href={'/privacy'}>Privacy Policy </a>
                      so that they may address your enquiry and respond to you
                      by means of the method which our legal provider considers
                      appropriate in the circumstances which may be by post,
                      email or by telephone.
                    </li>
                    <li className="mt-2">
                      4.8.3 Our legal provider may charge you for any advice or
                      services they provide to you. You should discuss any
                      charges that may apply with our legal provider at the time
                      that you submit your enquiry.
                    </li>
                    <li className="mt-2">
                      4.8.4 You will be responsible for paying any applicable
                      charges directly to our legal provider. We do not collect
                      payments on their behalf and you cannot apply any fees or
                      charges you have paid to us to any amounts you owe or come
                      to owe to our legal provider.
                    </li>
                    <li className="mt-2">
                      4.8.5 Our making available of a Basic Legal Advice service
                      is not a recommendation by us for you to seek the advice
                      or to use the services of our legal provider. We make no
                      representation or warranty about their advice or provision
                      of services, and we disclaim all responsibility and
                      liability for their advice or provision of services, or
                      failure to advise or provide services. To the maximum
                      extent permitted by applicable law, in no event shall we
                      be liable, answerable or accountable for any loss or
                      damage resulting from any services, advice or assistance
                      given to you by our legal provider or their failure to
                      advise or provide services.
                    </li>
                  </ul>
                </li>
                <li className="mt-2">
                  4.9 Loyalty Programme Service:
                  <ul className="list-none pl-10 mt-5">
                    <li className="mt-2">
                      4.9.1 Our loyalty programme comprises an online directory
                      service, wherein, from time to time, we may list certain
                      promotions and offers by third-party merchants who provide
                      those promotions and offers.
                    </li>
                    <li className="mt-2">
                      4.9.2 We are not affiliated with any of the merchants who
                      provide the promotions and offers listed in the loyalty
                      programme of our website, we do not receive any commission
                      for any of the promotions and offers listed, and we do not
                      share any information about our members with those parties
                      whose promotions and offers we list.
                    </li>
                    <li className="mt-2">
                      4.9.3 We have no control over and are not responsible for
                      the promotions and offers on our website nor the
                      third-party merchants that provide them, including whether
                      those merchants provide the promotions and offers in the
                      manner and according to any terms and conditions stated by
                      them in the promotions or offers listed (or at all).
                    </li>
                    <li className="mt-2">
                      4.9.4 We do not guarantee that any promotions or offers
                      listed on our website will be available at the time that
                      you view them or seek to take them up or otherwise
                      participate in them.
                    </li>
                    <li className="mt-2">
                      4.9.5 If you choose to view, take up or otherwise
                      participate in a promotion or offer listed in our loyalty
                      programme then you do so by means of a separate contract
                      with the merchant that provides the promotion or offer
                      concerned and not with us.
                    </li>
                    <li className="mt-2">
                      4.9.6 Before you view, take up or otherwise participate in
                      a promotion or offer then you should do your own research
                      to determine that the promotion or offer is suitable for
                      you and that you are happy with both the merchant that
                      provides that promotion or offer and with the products
                      and/or services they provide. You agree that we shall not
                      be responsible or liable for any loss or damage of any
                      sort incurred as a result of any dealings you have with
                      any merchants or third-parties whose promotions or offers
                      we list in our loyalty programme.
                    </li>
                    <li className="mt-2">
                      4.9.7 We are not liable or responsible for the third party
                      content on our site, which includes the promotions and
                      offers we list. When you view, take up or otherwise
                      participate in a promotion or offer listed in our loyalty
                      programme you will leave our website. We have no control
                      over and accept no responsibility or liability for the
                      material on any website which is not under our control and
                      this includes the websites of the merchants and
                      third-parties that offer the promotions and offers listed
                      on our website.
                    </li>
                  </ul>
                </li>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section5"
            >
              5. CLOSING YOUR ACCOUNT
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                5.1 If your free trial or Subscription expires or ends and your
                Subscription is not renewed on its Automatic Subscription
                Billing Date then your account will remain active for one (1)
                year, after which your account may be closed by us without
                warning.
              </li>
              <li className="mt-2">
                5.2 You may also close your account at any time by{' '}
                <a href={'/unsubscribe'}>requesting account closure </a>
                from us, though doing so will end your free trial or
                Subscription immediately without any right to a refund of any
                Subscription Fee you have paid.
              </li>
              <li className="mt-2">
                5.3 Where any of our Services result in a supply of digital
                content to you and that digital content is made available to you
                in your account then you will be able to access that digital
                content in your account so long as your account remains open.
              </li>
              <li className="mt-2">
                5.4 However, if your account is closed, whether by us or at your
                request, all data within it including any digital content will
                be subject to deletion without warning.
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section6"
            >
              6. YOUR RIGHTS AND REMEDIES AS A CONSUMER
            </h3>
            <p>
              This section applies to consumers only. Business customers should
              refer to section 10 below for information applicable to business
              customers.
            </p>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                6.1 Our legal duties:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    6.1.1 We honour our legal duty to provide consumers with
                    products that are as described on our website and that meet
                    all the requirements imposed by law.
                  </li>
                  <li className="mt-2">
                    6.1.2 If your product is Digital Content, for example a Land
                    Registry title, the Consumer Rights Act 2015 says digital
                    content must be as described, fit for purpose and of
                    satisfactory quality. If your Digital Content is faulty, you
                    are entitled to a repair or a replacement. If the fault
                    can’t be fixed, or if it has not been fixed within a
                    reasonable time and without significant inconvenience, you
                    can get some or all of your money back. If you can show the
                    fault has damaged your device and we haven’t used reasonable
                    care and skill, you may be entitled to a repair or
                    compensation (see <a href={'#section8'}>section 8</a>
                    ).
                  </li>
                  <li className="mt-2">
                    6.1.3 If your product is Services, for example the renewal
                    of your driving licence, the Consumer Rights Act 2015 says
                    you can ask us to repeat or fix a service if it is not
                    carried out with reasonable care and skill, or get some
                    money back if we can’t fix it. If a price hasn’t been agreed
                    upfront, what you are asked to pay must be reasonable. If a
                    time hasn’t been agreed upfront, it must be carried out
                    within a reasonable time.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                6.2 Obtaining a refund:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    6.2.1 If you are a consumer, you have a legal right to
                    change your mind. Subject to the following paragraphs, you
                    may withdraw from our contract for any or no reason at all
                    within fourteen (14) calendar days of being charged the
                    Subscription Fee (the Withdrawal Period).
                  </li>
                  <li className="mt-2">
                    6.2.2 If you wish to withdraw from our contract and obtain a
                    refund you must do so prior to the expiry of the Withdrawal
                    Period by <a href={'/contacts'}>contacting us </a>
                    and making an unequivocal statement to us, or by returning a
                    completed{' '}
                    <a href={'#cancelationForm'}>cancellation form </a>
                    to us.
                  </li>
                  <li className="mt-2">
                    6.2.3 However, you will not be able to exercise your right
                    to withdraw after any Service that you have ordered from us
                    has been fully performed if performance began with your
                    express request for immediate performance and your
                    acknowledgement that you will lose your rights of withdrawal
                    from the contract once the Service has been fully performed.
                  </li>
                  <li className="mt-2">
                    6.2.4 In the event you exercise your right to withdraw after
                    performance of any Service that you have ordered from us has
                    been commenced but before it has been fully performed then
                    you may only receive a partial refund to account for any
                    reasonable costs and/or Government Fees we have already
                    incurred.
                  </li>
                  <li className="mt-2">
                    6.2.5 If you withdraw from our contract in accordance with
                    this section then any refund due to you will be paid to you
                    within fourteen (14) days of the date you informed us that
                    you wished to withdraw. We will make any refund due by the
                    method you used for your payment of the Subscription Fee.
                  </li>
                  <li className="mt-2">
                    6.2.6 If you cancel or withdraw from our contract after the
                    expiry of the Withdrawal Period then you will not be
                    entitled to any refund.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                6.3 Cancellation form:
                <div className="mx-auto border-2 border-black p-5 my-4 w-2/3">
                  <p>
                    Complete and return this form only if you wish to withdraw
                    from the contract.
                  </p>
                  <br />
                  <p>
                    To [COMPANY NAME, ADDRESS, TELEPHONE NUMBER AND, WHERE
                    AVAILABLE, FAX NUMBER AND E-MAIL ADDRESS]:
                  </p>
                  <br />
                  <p>
                    I hereby give notice that I want to cancel my contract for
                    the supply of your Services.
                  </p>
                  <br />
                  <p>Ordered on/received on (delete as appropriate):</p>
                  <br />
                  <p>Name of consumer: </p>
                  <br />
                  <p>Address of consumer:</p>
                  <br />
                  <p>
                    Signature of consumer (only if this form is notified on
                    paper):
                  </p>
                  <br />
                  <p>Date:</p>
                </div>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section7"
            >
              7. COMPLAINTS AND RESOLVING DISPUTES
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                7.1 Complaints:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    7.1.1 If you have a complaint about our Services, please
                    tell us by using our contact details as set out in{' '}
                    <a href={'#section1.2'}>section 1.2 </a>
                    above. We aim to respond to all complaints within 7 days.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                7.2 Resolving disputes without going to court:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    7.2.1 If you are a consumer then you may be able to resolve
                    any dispute with us by means of Alternative Dispute
                    Resolution.
                  </li>
                  <li className="mt-2">
                    7.2.2 This is an optional process where an independent body
                    considers the facts of a dispute and seeks to resolve it,
                    without you having to go to court.
                  </li>
                  <li className="mt-2 flex flex-wrap">
                    7.2.3 You can submit a complaint to {companyName} through
                    their website at {HOSTNAME}.
                  </li>
                </ul>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section8"
            >
              8. COMPLAINTS AND RESOLVING DISPUTES
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                8.1 Our liability to consumers:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    8.1.1 Subject to the limit set out in this section, we may
                    be responsible for losses you suffer caused by us breaking
                    this contract unless the loss is:
                    <ul className="list-none pl-10 mt-5">
                      <li className="mt-2">
                        8.1.1.1 <span className="font-bold">Unexpected</span>.
                        It was not obvious that it would happen and nothing you
                        said to us and that we acknowledged in writing to you
                        before we accepted your order for a subscription or
                        before providing you with a free trial meant we should
                        have expected it (so, in law, the loss was
                        unforeseeable).
                      </li>
                      <li className="mt-2">
                        8.1.1.2{' '}
                        <span className="font-bold">
                          Caused by a delaying event outside our control
                        </span>
                        . As long as we have taken steps to contact you as soon
                        as possible to let you know and have done what we can to
                        reduce the delay.
                      </li>
                      <li className="mt-2">
                        8.1.1.3 <span className="font-bold">Avoidable</span>.
                        Something you could have avoided by taking reasonable
                        action. For example, damage to your own digital content
                        or device, which was caused by digital content we
                        supplied and which you could have avoided by following
                        our advice to apply a free update or by correctly
                        following installation or maintenance instructions or by
                        having the minimum system requirements advised.
                      </li>
                      <li className="mt-2">
                        8.1.1.4{' '}
                        <span className="font-bold">A business loss</span>. Our
                        liability for any loss you suffer in connection with
                        your trade, business, craft or profession is limited, as
                        described in Our liability to businesses (
                        <a href={'#section8.2'}>section 8.2</a>
                        ).
                      </li>
                    </ul>
                    <li className="mt-2">
                      8.1.2 Our total liability to you for all losses arising
                      under or in connection with any contract between us,
                      whether in contract, tort (including negligence), breach
                      of statutory duty, or otherwise, shall be limited to the
                      Subscription Fee paid by you for the Subscription in which
                      the incident or Service giving rise to the loss arose.
                    </li>
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section8.2">
                8.2 Our liability to business:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    8.2.1 If you are a business, then, except in respect of the
                    losses described in Losses we never limit or exclude (
                    <a href={'#section8.3'}>section 8.3</a>
                    ):
                    <ul className="list-none pl-10 mt-5">
                      <li className="mt-2">
                        8.2.1.1 We shall not be liable to you, whether in
                        contract, tort (including negligence), breach of
                        statutory duty, or otherwise, for any loss of profit, or
                        any indirect or consequential loss arising under or in
                        connection with any contract between us; and
                      </li>
                      <li className="mt-2 flex flex-wrap">
                        8.2.1.2 Our total liability to you for all other losses
                        arising under or in connection with any contract between
                        us, whether in contract, tort (including negligence),
                        breach of statutory duty, or otherwise, shall be limited
                        to the Subscription Fee paid by you for the Subscription
                        in which the incident or Service giving rise to the loss
                        arose.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section8.3">
                8.3 Losses we never exclude:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    8.3.1 Nothing in these Terms shall limit or exclude our
                    liability for:
                    <ul className="list-none pl-10 mt-5">
                      <li className="mt-2">
                        8.3.1.1 Death or personal injury caused by our
                        negligence, or the negligence of our employees, agents
                        or subcontractors (as applicable);
                      </li>
                      <li className="mt-2 flex flex-wrap">
                        8.3.1.2 Fraud or fraudulent misrepresentation;
                      </li>
                      <li className="mt-2 flex flex-wrap">
                        8.3.1.3 Breach of the terms implied by section 12 of the
                        Sale of Goods Act 1979 or section 2 of the Supply of
                        Goods and Services Act 1982;
                      </li>
                      <li className="mt-2 flex flex-wrap">
                        8.3.1.4 Defective products under the Consumer Protection
                        Act 1987;
                      </li>
                      <li className="mt-2 flex flex-wrap">
                        8.3.1.5 Any matter in respect of which it would be
                        unlawful for us to exclude or restrict liability.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                8.4 No implied terms about goods:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    8.4.1 Except to the extent expressly stated in{' '}
                    <a href={'#section10.2'}>section 10.2 </a>
                    concerning{' '}
                    <a href={'#section10.3'}>
                      Your rights if you are a business
                    </a>
                    , we exclude all terms implied by sections 13 to 15 of the
                    Sale of Goods Act 1979 and sections 3 to 5 of the Supply of
                    Goods and Services Act 1982.
                  </li>
                </ul>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section9"
            >
              9. NOTICES
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                9.1 When we refer to “in writing” in these Terms, this includes
                email.
              </li>
              <li className="mt-2">
                9.2 Any notice or other communication given under or in
                connection with the contract must be in writing and be delivered
                personally, sent by pre-paid first class post or other next
                working day delivery service, or email.
              </li>
              <li className="mt-2">
                9.3 Our <a href={'/contacts'}>contact details</a>
                as provided in <a href={'#section1.2'}>section 1.2 </a>
                above may be used to contact us.
              </li>
              <li className="mt-2">
                9.4 A notice or other communication is deemed to have been
                received:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    9.4.1 if delivered personally, on signature of a delivery
                    receipt;
                  </li>
                  <li className="mt-2">
                    9.4.2 if sent by pre-paid first class post or other next
                    working day delivery service, at 9.00 am on the second
                    working day after posting; or
                  </li>
                  <li className="mt-2 flex flex-wrap">
                    9.4.3 if sent by email, at 9.00 am the next working day
                    after transmission.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                9.5 In proving the service of any notice, it will be sufficient
                to prove, in the case of a letter, that such letter was properly
                addressed, stamped and placed in the post and, in the case of an
                email, that such email was sent to the specified email address
                of the addressee.
              </li>
              <li className="mt-2">
                9.6 The provisions of this clause will not apply to the service
                of any proceedings or other documents in any legal action.
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section10"
            >
              10. ADDITIONAL TERMS AND INFORMATION FOR BUSINESS CUSTOMERS
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                10.1 Business customers do not have all the same rights as
                consumers:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    10.1.1 For example, business customers can’t cancel their
                    orders, they have different rights where there is a problem
                    with a product or Service and we don’t compensate them in
                    the same way for losses caused by us or our products or
                    Services.
                  </li>
                  <li className="mt-2">
                    10.1.2 Where a term applies just to businesses or just to
                    consumers, this is clearly stated in these Terms.
                  </li>
                  <li className="mt-2">
                    10.1.3 You are a business customer if you are transacting
                    with us wholly or mainly for use in connection with your
                    trade, business, craft or profession, even if you are an
                    individual.
                  </li>
                </ul>
              </li>
              <li className="mt-2" id="section10.2">
                10.2 Your rights if you are a business:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    10.2.1 We warrant that on delivery, and for a period of 1
                    month from the date of delivery (warranty period), any
                    products which are goods shall:
                    <ul className="list-none pl-10 mt-5">
                      <li className="mt-2">
                        10.2.1.1 Conform with their description and any relevant
                        specification;
                      </li>
                      <li className="mt-2">
                        10.2.1.2 Be free from material defects in design,
                        material and workmanship;
                      </li>
                      <li className="mt-2">
                        10.2.1.3 Be of satisfactory quality (within the meaning
                        of the Sale of Goods Act 1979); and
                      </li>
                      <li className="mt-2">
                        10.2.1.4 Be fit for any purpose held out by us.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                10.3 Entire agreement:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    10.3.1 If you are a business customer these Terms constitute
                    the entire agreement between us in relation to your
                    purchase. You acknowledge that you have not relied on any
                    statement, promise, representation, assurance or warranty
                    made or given by us or on our behalf which is not set out in
                    these Terms and that you have no claim for innocent or
                    negligent misrepresentation or negligent misstatement based
                    on any statement in this agreement.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                10.4 No set-off rights:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    10.4.1 If you are a business customer, all amounts due under
                    this agreement (from you to us or from us to you) shall be
                    paid in full without any set-off, counterclaim, deduction or
                    withholding (other than any deduction or withholding of tax
                    as required by law).
                  </li>
                </ul>
              </li>
            </ul>

            <h3
              className="text-lg leading-8 font-medium tracking-tight text-gray-900 mt-8 mb-5 sm:text-2xl sm:leading-10"
              id="section11"
            >
              11. GENERAL
            </h3>
            <ul className="list-none pl-10 mt-5">
              <li className="mt-2">
                11.1 Governing law and jurisdiction:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    11.1.1 If These terms are governed by English law.
                  </li>
                  <li className="mt-2">
                    11.1.2 If you are a consumer then, wherever you live, you
                    can bring claims against us in the English courts and if you
                    live in Wales, Scotland or Northern Ireland, you can also
                    bring claims against us in the courts of the country you
                    live in. If you are a consumer we can claim against you in
                    the courts of the country you live in.
                  </li>
                  <li className="mt-2">
                    11.1.3 If you are a business you irrevocably agree to submit
                    all disputes arising out of or in connection with our
                    contract with you to the exclusive jurisdiction of the
                    English courts.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                11.2 Nobody else has any rights under this contract:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    11.2.1 This contract is between you and us. Nobody else can
                    enforce it and neither of us will need to ask anybody else
                    to sign-off on ending or changing it.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                11.3 If a court invalidates some of this contract, the rest of
                it will still apply:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    11.3.1 If a court or other authority decides that some of
                    these terms are unlawful, the rest will continue to apply.
                  </li>
                </ul>
              </li>
              <li className="mt-2">
                11.4 Even if we delay in enforcing this contract, we can still
                enforce it later:
                <ul className="list-none pl-10 mt-5">
                  <li className="mt-2">
                    11.4.1 We might not immediately chase you for not doing
                    something (like paying) or for doing something you&apos;re
                    not allowed to, but that doesn’t mean we can&apos;t do it
                    later.
                  </li>
                </ul>
              </li>
            </ul>

            <br></br>
            <p className="mt-5">Last updated: 25 Oct 2022</p>
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  return getTheme(context);
};
